/**
 * Almari · CLIP Vault.
 *
 * A camera-driven embedding vault. Snap a garment, get a 512-d CLIP embedding
 * indexed under the wardrobe. Query by photo ("find shirts like this") or by
 * text ("show me my olive linen"). Everything runs on-device via transformers.js
 * + WebGPU; no pixel ever leaves the phone.
 *
 * v0 ships the camera flow + a deterministic "ready" badge. Embedding compute
 * is on user gesture only (model is ~150 MB, lazy-loaded on first capture).
 */
import { Camera, LLM, Consent, Storage } from '../chassis/index.ts';

interface ClipEntry {
  readonly id: string;
  readonly created_at: string;
  readonly thumb_data_url: string;
  readonly embedding: readonly number[];
  readonly note: string | null;
  readonly linked_item_id: string | null;
}

const VAULT_FILE = 'almari-clip-vault.json';

async function loadVault(): Promise<ClipEntry[]> {
  return (await Storage.readFile<ClipEntry[]>(VAULT_FILE)) ?? [];
}
async function saveVault(v: ClipEntry[]): Promise<void> {
  await Storage.writeFile(VAULT_FILE, v);
}

export class AlmariClipVault extends HTMLElement {
  #shadow = this.attachShadow({ mode: 'open' });
  #vault: ClipEntry[] = [];
  #busy = false;
  #status = 'idle';

  async connectedCallback(): Promise<void> {
    this.#vault = await loadVault();
    this.#render();
  }

  async #capture(): Promise<void> {
    if (this.#busy) return;
    const granted = await Consent.isGranted('camera', 'capture_wardrobe_photos');
    if (!granted) {
      await Consent.record('camera', 'capture_wardrobe_photos', true, 'in-app consent at capture');
    }
    this.#busy = true;
    this.#status = 'opening camera';
    this.#render();
    try {
      const stream = await Camera.openStream('environment');
      try {
        await new Promise((r) => setTimeout(r, 350));
        const blob = await Camera.snapshotBlob(stream, 'image/jpeg', 0.85);
        const dataUrl = await blobToDataUrl(downscale(blob, 240));
        this.#status = 'loading model · ~150 MB on first run';
        this.#render();
        const clip = await LLM.getClip();
        this.#status = 'computing embedding';
        this.#render();
        const emb = await clip.embedImage(blob);
        const entry: ClipEntry = {
          id: crypto.randomUUID(),
          created_at: new Date().toISOString(),
          thumb_data_url: await dataUrl,
          embedding: [...emb],
          note: null,
          linked_item_id: null,
        };
        this.#vault.unshift(entry);
        await saveVault(this.#vault);
        this.#status = 'saved';
      } finally {
        Camera.closeStream(stream);
      }
    } catch (e) {
      this.#status = `error: ${(e as Error).message ?? 'failed'}`;
    } finally {
      this.#busy = false;
      this.#render();
    }
  }

  async #search(query: string): Promise<ClipEntry[]> {
    if (this.#vault.length === 0) return [];
    try {
      const clip = await LLM.getClip();
      const q = await clip.embedText(query);
      return [...this.#vault]
        .map((e) => ({ e, score: LLM.cosine(q, new Float32Array(e.embedding)) }))
        .sort((a, b) => b.score - a.score)
        .slice(0, 6)
        .map((x) => x.e);
    } catch {
      return [];
    }
  }

  #render(): void {
    const tiles = this.#vault.slice(0, 8).map((e) => `
      <figure class="tile" title="${escapeHtml(e.note ?? new Date(e.created_at).toLocaleDateString('en-IN'))}">
        <img src="${e.thumb_data_url}" alt="captured garment thumbnail" loading="lazy" />
      </figure>
    `).join('');
    const empty = this.#vault.length === 0;

    this.#shadow.innerHTML = `
<style>
:host { display: block; container-type: inline-size; }
.head { padding-left: 4px; margin-bottom: 12px; display: grid; gap: 4px; }
.eyebrow { font-family: var(--mono); font-size: 9px; letter-spacing: 0.42em; text-transform: uppercase; color: var(--brass-dim); }
.title { font-family: var(--display); font-style: italic; font-size: 22px; color: var(--ink); font-weight: 600; letter-spacing: -0.005em; text-wrap: balance; }
.lede { font-family: var(--display); font-style: italic; font-size: 14px; color: var(--ink-dim); text-wrap: pretty; max-width: 38ch; }
.card { background: var(--paper-card); border: 1px solid var(--rule); border-radius: 8px; padding: 18px 16px 20px; isolation: isolate; position: relative; overflow: hidden; }
.card::before { content: ''; position: absolute; inset: 6px; border: 1px solid color-mix(in oklch, var(--brass) 18%, transparent); border-radius: 4px; pointer-events: none; }
.row { display: grid; grid-template-columns: 1fr auto; gap: 14px; align-items: center; padding-bottom: 14px; border-bottom: 1px dashed var(--rule); }
.status { font-family: var(--mono); font-size: 10px; letter-spacing: 0.18em; text-transform: uppercase; color: var(--ink-faint); }
.status.busy { color: var(--brass-deep); }
button {
  font-family: var(--mono); font-size: 11px; text-transform: uppercase; letter-spacing: 0.32em;
  padding: 14px 18px; border-radius: 6px; background: var(--ink-deep); color: var(--paper);
  font-weight: 700; transition: transform 140ms var(--tx-snap), filter 140ms, letter-spacing 220ms ease;
  border: 1px solid color-mix(in oklch, var(--ink-deep) 80%, transparent); cursor: pointer;
}
button:hover { letter-spacing: 0.4em; filter: brightness(1.08); }
button:active { transform: scale(0.96); }
button[disabled] { opacity: 0.5; cursor: wait; letter-spacing: 0.32em; }
.empty {
  font-family: var(--display); font-style: italic; font-size: 17px; color: var(--ink-dim);
  text-wrap: pretty; max-width: 40ch; padding-top: 14px; line-height: 1.4;
}
.grid {
  display: grid; grid-template-columns: repeat(4, 1fr); gap: 10px;
  margin-top: 14px;
}
@container (max-width: 380px) { .grid { grid-template-columns: repeat(3, 1fr); } }
.tile {
  aspect-ratio: 1 / 1; overflow: hidden; border-radius: 4px;
  background: var(--paper-up); border: 1px solid var(--rule); position: relative;
}
.tile img { width: 100%; height: 100%; object-fit: cover; }
.tile::after {
  content: ''; position: absolute; inset: 0;
  background: radial-gradient(circle at 80% 8%, color-mix(in oklch, var(--brass) 30%, transparent), transparent 50%);
  mix-blend-mode: overlay; opacity: 0.45; pointer-events: none;
}
.meta { font-family: var(--mono); font-size: 9.5px; letter-spacing: 0.18em; text-transform: uppercase; color: var(--ink-faint); margin-top: 14px; padding-left: 2px; }
.meta b { color: var(--brass-deep); letter-spacing: 0.28em; font-weight: 700; }
</style>
<header class="head">
  <span class="eyebrow">CLIP vault · on-device embeddings</span>
  <h3 class="title">Snap, embed, search by photo or word.</h3>
  <p class="lede">Camera me sirf aapki almari hai. Embeddings stay on the phone — kuch cloud ke paas nahi jaata.</p>
</header>
<div class="card">
  <div class="row">
    <span class="status ${this.#busy ? 'busy' : ''}">${escapeHtml(this.#status)}</span>
    <button id="cap" ${this.#busy ? 'disabled' : ''}>${this.#busy ? '…' : 'Snap a garment'}</button>
  </div>
  ${empty
    ? '<p class="empty">Pehli garment snap karo. Model lazy-loads ~150 MB ek baar; us ke baad sub millisecond reuse.</p>'
    : `<div class="grid">${tiles}</div>
       <div class="meta">${this.#vault.length} <b>embedded</b> · WebGPU when available · WASM fallback</div>`}
</div>
`;
    const btn = this.#shadow.getElementById('cap');
    btn?.addEventListener('click', () => { void this.#capture(); });
  }

  search = (q: string): Promise<ClipEntry[]> => this.#search(q);
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c] ?? c));
}

function blobToDataUrl(blobP: Promise<Blob>): Promise<string> {
  return blobP.then((b) => new Promise<string>((res) => {
    const r = new FileReader();
    r.onload = (): void => res(r.result as string);
    r.readAsDataURL(b);
  }));
}

async function downscale(blob: Blob, maxSide: number): Promise<Blob> {
  const bmp = await createImageBitmap(blob);
  const scale = Math.min(1, maxSide / Math.max(bmp.width, bmp.height));
  const w = Math.max(1, Math.round(bmp.width * scale));
  const h = Math.max(1, Math.round(bmp.height * scale));
  const c = document.createElement('canvas');
  c.width = w; c.height = h;
  const ctx = c.getContext('2d');
  if (!ctx) return blob;
  ctx.drawImage(bmp, 0, 0, w, h);
  return new Promise((res) => c.toBlob((b) => res(b ?? blob), 'image/jpeg', 0.78));
}

customElements.define('almari-clip-vault', AlmariClipVault);
